import { modals } from '@mantine/modals';
import { useDebtor } from 'app/Private/Clients/Detail/client-atom';
import type { ActionButtonDefinition } from 'app/Private/Clients/Detail/UnhandledInvoicesTab/use-build-action-buttons';
import { useInvalidateUnhandledClients } from 'app/Private/Clients/Detail/UnhandledInvoicesTab/use-invalidate-unhandled-clients';
import { tKeys, useTranslate } from 'locales/index';
import type { InvoiceAction, UnhandledInvoice } from 'modules/AtRisk/api/use-load-unhandled-client';
import posthog from 'posthog-js';
import { captureEvent } from 'shared/analytics/capture-event';
import ListResModal from 'shared/components/DeleteModal/ListResModal';
import { invoiceActions } from 'store/invoice/invoice.actions';

export function computeCommonProps(
  action: InvoiceAction,
  invoices: Array<UnhandledInvoice>,
): Pick<ActionButtonDefinition, 'isEnabled' | 'recommended' | 'eligibleInvoiceIds'> {
  // Per-invoice availableActions can act; the engine's debtor-level availability only highlights.
  const eligibleInvoiceIds = invoices
    .filter(invoice => invoice.availableActions?.includes(action.type) ?? false)
    .map(invoice => invoice.id);

  return {
    isEnabled: eligibleInvoiceIds.length > 0,
    recommended: action.isAvailable ? ('engine' as const) : null,
    eligibleInvoiceIds,
  };
}

export function useBatchAction() {
  const t = useTranslate();
  const { id } = useDebtor();
  const invalidateUnhandledClients = useInvalidateUnhandledClients();

  return (action_type: string, invoiceIds: Array<string>, values: Record<string, unknown>) => {
    captureBatchAction(action_type, invoiceIds);

    invoiceActions.actionsBatchAction({
      data: {
        invoice_ids: invoiceIds,
        action_type,
        ...values,
      },
      callback: (res: unknown) => {
        invalidateUnhandledClients(String(id));
        if (!Array.isArray(res)) return;
        modals.open({
          size: 'auto',
          title: t(tKeys.RESULT),
          children: <ListResModal data={res} />,
        });
      },
    });
  };
}

// The batch endpoint calls a dispute 'dispute'; the engine every other button
// reports under calls it 'mark_as_disputed'. Analytics follows the engine.
const ENGINE_ACTION_TYPES: Record<string, string> = { dispute: 'mark_as_disputed' };

function captureBatchAction(action_type: string, invoiceIds: Array<string>) {
  captureEvent(posthog, 'collection:invoices_treated', {
    action_type: ENGINE_ACTION_TYPES[action_type] ?? action_type,
    invoices_treated_count: invoiceIds.length,
  });
}
